import React, {useMemo} from "react";
import {SectorsRow} from "../table/SectorsRow";
import {Table} from "../../../elements/Tables";
import SortArrow from "../../../elements/SortArrow";
import Heading from "../table/Heading";
import {SentimentChart} from "./SentimentChart";
import {VolumeChart} from "./VolumeChart";

const sentiment = (s) => s.percentageLong;
const volume = (s) => s.percentageOfOpenIdeas;
const maxVolume = (sectors) => {
    return Math.max.apply(null, sectors.map(volume));
};

const isNotAvailable = (s) => s === null;

const sortWithFallbackForUnavailable = (a, b, extractor, fallbackSort) => {
    const valueForA = extractor(a);
    const valueForB = extractor(b);
    if (isNotAvailable(valueForA) && isNotAvailable(valueForB)) {
        return fallbackSort(a, b);
    }

    return isNotAvailable(valueForA) ? -1 : isNotAvailable(valueForB) ? 1 : valueForB - valueForA;
};

const nameCompare = (a, b) => a.name.localeCompare(b.name);

const SORT_FUNCTIONS = {
    sector: {sortFn: (a, b) => nameCompare(a, b), alphabetic: true},
    volume: {sortFn: (a, b) => sortWithFallbackForUnavailable(a, b, volume, nameCompare)},
    sentiment: {sortFn: (a, b) => sortWithFallbackForUnavailable(a, b, sentiment, nameCompare)}
};

export const SectorsNowTable = ({today, sectors: {sortField, sortReversed}, sectorsActions}) => {
    function sortIcon(fieldName) {
        if (fieldName !== sortField) {
            return "";
        }
        let arrowUp = sortReversed;
        if (SORT_FUNCTIONS[fieldName].alphabetic) {
            arrowUp = !arrowUp;
        }
        return arrowUp ? <SortArrow dir="ASC"/> : <SortArrow dir="DESC"/>;
    }

    const maxIdeaVolume = maxVolume(today);

    const {sortedSectors, sortBySector, sortBySentiment, sortByVolume} = useMemo(() => {
        function sortBy(fieldName) {
            sectorsActions.changeSort(fieldName, !sortReversed && fieldName === sortField);
        }

        return ({
            sortedSectors: (function () {
                const sortFn = SORT_FUNCTIONS[sortField].sortFn;
                let sortedSectors = today.sort(sortFn);
                if (sortReversed) {
                    sortedSectors = sortedSectors.reverse();
                }
                return sortedSectors;
            })(),
            sortBySector() {
                sortBy("sector");
            },
            sortBySentiment() {
                sortBy("sentiment");
            },
            sortByVolume() {
                sortBy("volume")
            }
        });
    }, [sortField, sortReversed]);

    return (
        <Table className="t-sectors-table-chart">
            <thead>
            <tr>
                <Heading className="t-heading-sector"
                         onClick={sortBySector}
                         sortIcon={sortIcon("sector")}
                         text="Sector"/>

                <Heading className="t-heading-sentiment"
                         onClick={sortBySentiment}
                         sortIcon={sortIcon("sentiment")}
                         text="% Short/Long"/>

                <Heading className="t-heading-volume"
                         onClick={sortByVolume}
                         sortIcon={sortIcon("volume")}
                         text="Share of Open Ideas"/>
            </tr>
            </thead>
            <tbody className="t-sectors-table">
            {
                sortedSectors.map(sector => {
                    return <SectorsRow key={sector.name}
                                       name={sector.name}
                                       sentimentChart={
                                    <SentimentChart {...sector}/>
                                }
                                       volumeChart={
                                    <VolumeChart maxVolume={maxIdeaVolume}
                                                 {...sector}/>
                                }/>;
                })
            }
            </tbody>
        </Table>
    );
};
